import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, parserRuleService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { ParserRule, CreateParserRuleData } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const ParserRulesPage = () => {
    const { documentType, schemaId } = useParams<{ documentType: string; schemaId: string }>();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [schema, setSchema] = useState(documentSchemaService.getById(schemaId || ''));
    const [rules, setRules] = useState<ParserRule[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<ParserRule | null>(null);

    const [ruleFormData, setRuleFormData] = useState<CreateParserRuleData>({
        document_schema_id: schemaId || '',
        rule_name: '',
        natural_language_rule: '',
        description: '',
        priority: 0,
        is_active: true,
    });

    const currentDocType = schema ? DOCUMENT_TYPES.find((dt) => dt.key === schema.document_type) : null;

    useEffect(() => {
        if (schemaId) {
            loadData();
        }
    }, [schemaId]);

    const loadData = () => {
        setIsLoading(true);
        if (schemaId) {
            const schemaData = documentSchemaService.getById(schemaId);
            setSchema(schemaData);
            const schemaRules = parserRuleService.getBySchemaId(schemaId);
            setRules(schemaRules);
        }
        setIsLoading(false);
    };

    const handleOpenModal = (rule?: ParserRule) => {
        if (rule) {
            setEditingRule(rule);
            setRuleFormData({
                document_schema_id: rule.document_schema_id,
                rule_name: rule.rule_name,
                natural_language_rule: rule.natural_language_rule,
                description: rule.description,
                priority: rule.priority,
                is_active: rule.is_active,
            });
        } else {
            setEditingRule(null);
            setRuleFormData({
                document_schema_id: schemaId || '',
                rule_name: '',
                natural_language_rule: '',
                description: '',
                priority: 0,
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRule(null);
    };

    const handleSave = () => {
        if (editingRule) {
            parserRuleService.update(editingRule.parser_rule_id, {
                ...ruleFormData,
                parser_rule_id: editingRule.parser_rule_id,
            });
        } else {
            parserRuleService.create(ruleFormData);
        }
        loadData();
        handleCloseModal();
    };

    const handleDelete = (id: string) => {
        parserRuleService.delete(id);
        loadData();
    };

    if (isLoading || !schema) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/types"
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.documentTypes')}
                </Link>
                <span className="text-tertiary">/</span>
                <Link
                    to={`/admin/documents/types/${documentType}/schemas`}
                    className="text-tertiary hover:text-primary"
                >
                    {currentDocType?.label || documentType}
                </Link>
                <span className="text-tertiary">/</span>
                <Link
                    to={`/admin/documents/types/${documentType}/schemas`}
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.schemas')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{schema.schema_name}</span>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{t('parserRules.title')}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('parserRules.title')} - {schema.schema_name}
                    </h2>
                    <p className="text-tertiary mt-1">{t('parserRules.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('parserRules.actions.add')}
                </Button>
            </div>

            {/* Rules Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('parserRules.title')} (${rules.length})`}
                    description={t('parserRules.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('parserRules.modal.fields.ruleName')} isRowHeader />
                        <Table.Head label={t('parserRules.modal.fields.naturalLanguageRule')} />
                        <Table.Head label={t('parserRules.priority')} />
                        <Table.Head label={t('parserRules.modal.fields.isActive')} />
                        <Table.Head label={t('parserRules.modal.fields.description')} />
                        <Table.Head label={t('schemas.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {rules.map((rule) => (
                            <Table.Row key={rule.parser_rule_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{rule.rule_name}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-primary max-w-md">{rule.natural_language_rule}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">{rule.priority}</Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    {rule.is_active ? (
                                        <Badge color="success" size="sm">{t('parserRules.status.active')}</Badge>
                                    ) : (
                                        <Badge color="error" size="sm">{t('parserRules.status.inactive')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{rule.description || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(rule)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('parserRules.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(rule.parser_rule_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('parserRules.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {rules.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('parserRules.modal.noRules')}</p>
                    </div>
                </div>
            )}

            {/* Modal for Add/Edit Rule */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingRule ? t('parserRules.modal.editRule') : t('parserRules.modal.addRule')}
                                </h2>

                                <div className="space-y-4">
                                    <Input
                                        label={t('parserRules.modal.fields.ruleName')}
                                        placeholder={t('parserRules.modal.fields.ruleNamePlaceholder')}
                                        value={ruleFormData.rule_name}
                                        onChange={(value) => setRuleFormData({ ...ruleFormData, rule_name: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('parserRules.modal.fields.naturalLanguageRule')}
                                        </label>
                                        <textarea
                                            className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                            rows={8}
                                            value={ruleFormData.natural_language_rule}
                                            onChange={(e) => setRuleFormData({ ...ruleFormData, natural_language_rule: e.target.value })}
                                            placeholder={t('parserRules.modal.fields.naturalLanguageRulePlaceholder')}
                                        />
                                        <p className="text-xs text-tertiary mt-1">
                                            {t('parserRules.modal.fields.naturalLanguageRuleHelp')}
                                        </p>
                                    </div>

                                    <Input
                                        label={t('parserRules.modal.fields.description')}
                                        placeholder={t('parserRules.modal.fields.descriptionPlaceholder')}
                                        value={ruleFormData.description || ''}
                                        onChange={(value) => setRuleFormData({ ...ruleFormData, description: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('parserRules.modal.fields.priority')}
                                            value={String(ruleFormData.priority || 0)}
                                            onChange={(value) => setRuleFormData({ ...ruleFormData, priority: Number(value) || 0 })}
                                            size="md"
                                        />

                                        <div className="flex items-center gap-2 pt-6">
                                            <input
                                                type="checkbox"
                                                checked={ruleFormData.is_active !== undefined ? ruleFormData.is_active : true}
                                                onChange={(e) => setRuleFormData({ ...ruleFormData, is_active: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <label className="text-sm text-primary">{t('parserRules.modal.fields.isActive')}</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingRule ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
